'use client';

import React, {
  createContext, ReactNode, useContext, useMemo,
} from 'react';
import type { ProductRule } from '@/integrations/crystallize/helpers/product-rule-shape-helper';
import { ProductRuleService } from '@/ui/services/product-rule-service';

interface ProductRulesContextValue {
  productRuleService: ProductRuleService;
}

const NO_PRODUCT_RULES: ProductRule[] = [];

const ProductRulesContext = createContext<ProductRulesContextValue>({
  productRuleService: new ProductRuleService(NO_PRODUCT_RULES),
});

export interface ProductRulesProviderProps {
  /** The page's product rules: badges and who may buy. */
  productRules?: ProductRule[];
  children: ReactNode;
}

/**
 * Gives every product on the page its badges and purchase restrictions. The page passes plain
 * rules (server components cannot hand class instances to the client); the rule service is built here.
 */
export function ProductRulesProvider({
  productRules = NO_PRODUCT_RULES,
  children,
}: Readonly<ProductRulesProviderProps>) {
  const value = useMemo(
    () => ({ productRuleService: new ProductRuleService(productRules) }),
    [productRules],
  );

  return <ProductRulesContext.Provider value={value}>{children}</ProductRulesContext.Provider>;
}

export function useProductRuleService(): ProductRuleService {
  return useContext(ProductRulesContext).productRuleService;
}
